/**
 * InventoryPanel — toggleable overlay showing equipped items and carry weight.
 *
 * Reads equipment and weight data from the local player's Colyseus schema
 * fields (equippedWeapon, equippedArmor, equippedAccessory, inventoryWeight,
 * inventoryCapacity, encumbered). No server messages required — the schema
 * is already synced.
 *
 * Toggled via I key (routed through HudState INVENTORY_TOGGLE event).
 * Follows the same Container + show/hide pattern as PauseOverlay.
 *
 * Two renderings, chosen once at construction:
 *   - C5 flag ON  → a console sheet of dark sockets; an equipped slot is a
 *     wax seal stamped with the item's initial, an empty slot a faint dashed
 *     ring, and carry weight a wet ink stroke that turns red when encumbered.
 *   - C5 flag OFF → the original Phaser panel, untouched.
 * Both read the same InventorySnapshot; only presentation differs.
 */

import * as Phaser from 'phaser';
import { isC5UiEnabled } from './c5/c5Flag';
import {
  C5Panel,
  c5Note,
  c5Socket,
  c5WeightStroke,
  type C5WeightStroke,
} from './c5/C5Panel';
import {
  INVENTORY_BG,
  INVENTORY_BORDER,
  INVENTORY_SLOT_BG,
  INVENTORY_SLOT_EMPTY,
  HUD_TEXT_PRIMARY,
  HUD_TEXT_SECONDARY,
  PAL_YELLOW,
  PAL_RED,
  SLOT_COLORS,
  hexToCSS,
} from './palette';
import { getString } from './locale';
import { EquipmentSlot } from '@game/shared';

const PANEL_DEPTH = 2400;
const FADE_MS = 150;
const PANEL_WIDTH = 220;
const PANEL_HEIGHT = 260;
const SLOT_SIZE = 36;
const SLOT_GAP = 8;
const MARGIN = 16;

/** Data snapshot the panel reads each time it shows. */
export interface InventorySnapshot {
  equippedWeapon: string;
  equippedArmor: string;
  equippedAccessory: string;
  inventoryWeight: number;
  inventoryCapacity: number;
  encumbered: boolean;
}

export class InventoryPanel {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private visible = false;

  // Mutable labels refreshed on show()
  private weaponLabel!: Phaser.GameObjects.Text;
  private armorLabel!: Phaser.GameObjects.Text;
  private accessoryLabel!: Phaser.GameObjects.Text;
  private weightLabel!: Phaser.GameObjects.Text;

  // Slot backgrounds (tinted on refresh)
  private weaponSlotBg!: Phaser.GameObjects.Rectangle;
  private armorSlotBg!: Phaser.GameObjects.Rectangle;
  private accessorySlotBg!: Phaser.GameObjects.Rectangle;

  /** Called by UIScene to supply fresh player data. */
  private snapshotProvider: (() => InventorySnapshot | null) | null = null;

  // C5 rendering (flag ON). Null when the Phaser panel is in use.
  private panel: C5Panel | null = null;
  private c5Rows = new Map<EquipmentSlot, { row: HTMLDivElement; label: HTMLSpanElement }>();
  private c5Weight: C5WeightStroke | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.container = scene.add.container(0, 0);
    this.container.setScrollFactor(0);
    this.container.setDepth(PANEL_DEPTH);
    this.container.setVisible(false);
    this.container.setAlpha(0);

    if (isC5UiEnabled()) {
      this.buildC5Panel();
      return;
    }
    this.createPanel();
  }

  setSnapshotProvider(provider: () => InventorySnapshot | null): void {
    this.snapshotProvider = provider;
  }

  show(): void {
    if (this.visible) return;
    this.refresh();
    this.visible = true;
    if (this.panel) {
      this.panel.show();
      return;
    }
    this.container.setVisible(true);
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: FADE_MS,
    });
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    if (this.panel) {
      this.panel.hide();
      return;
    }
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: FADE_MS,
      onComplete: () => {
        this.container.setVisible(false);
      },
    });
  }

  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  /** C5 rendering: three sockets, an ink weight stroke, a paper hint. */
  private buildC5Panel(): void {
    const panel = new C5Panel({
      title: getString('hud.inventory.title'),
      variant: 'console',
      width: 260,
    });

    for (const slot of [EquipmentSlot.Weapon, EquipmentSlot.Armor, EquipmentSlot.Accessory]) {
      const row = document.createElement('div');
      row.className = 'c5-p-row';
      const label = document.createElement('span');
      label.className = 'c5-p-rowlabel';
      row.append(this.buildC5Socket(slot, ''), label);
      panel.body.appendChild(row);
      this.c5Rows.set(slot, { row, label });
    }

    this.c5Weight = c5WeightStroke(getString('hud.inventory.weight_label'));
    panel.body.appendChild(this.c5Weight.row);
    panel.body.appendChild(c5Note(getString('hud.inventory.toggle')));

    this.panel = panel;
  }

  /** One socket: wax seal stamped with the item's initial, or a dashed ring. */
  private buildC5Socket(slot: EquipmentSlot, itemType: string): HTMLElement {
    const slotName = getString(`hud.equipment.${slot}`);
    if (!itemType) {
      return c5Socket({ ariaLabel: `${slotName}: ${getString('hud.equipment.empty_slot')}` });
    }
    const itemName = getString(`hud.item.${itemType}`) || itemType;
    return c5Socket({
      glyph: [...itemName][0] ?? '',
      ariaLabel: `${slotName}: ${itemName}`,
      title: itemName,
    });
  }

  /** Repaint the C5 sockets and the weight stroke from a fresh snapshot. */
  private refreshC5(snap: InventorySnapshot | null): void {
    const equipped: Record<string, string> = {
      [EquipmentSlot.Weapon]: snap?.equippedWeapon ?? '',
      [EquipmentSlot.Armor]: snap?.equippedArmor ?? '',
      [EquipmentSlot.Accessory]: snap?.equippedAccessory ?? '',
    };

    for (const [slot, row] of this.c5Rows) {
      const itemType = equipped[slot] ?? '';
      const slotName = getString(`hud.equipment.${slot}`);
      const itemName = itemType
        ? getString(`hud.item.${itemType}`) || itemType
        : getString('hud.equipment.empty_slot');
      row.label.textContent = `${slotName}: ${itemName}`;
      row.row.replaceChild(this.buildC5Socket(slot, itemType), row.row.firstElementChild!);
    }

    if (!this.c5Weight) return;
    if (snap) {
      const text = getString('hud.inventory.weight')
        .replace('{0}', String(snap.inventoryWeight))
        .replace('{1}', String(snap.inventoryCapacity));
      this.c5Weight.set(snap.inventoryWeight, snap.inventoryCapacity, text, snap.encumbered);
    } else {
      this.c5Weight.set(0, 0, '');
    }
  }

  private createPanel(): void {
    const { width, height } = this.scene.scale;
    // Right-side panel so it doesn't overlap health/mana bars (top-left)
    const px = width - MARGIN - PANEL_WIDTH / 2;
    const py = height / 2;

    // Panel background
    const bg = this.scene.add.rectangle(px, py, PANEL_WIDTH, PANEL_HEIGHT, INVENTORY_BG, 0.92);
    bg.setStrokeStyle(2, INVENTORY_BORDER);
    this.container.add(bg);

    // Title
    const title = this.scene.add.text(px, py - PANEL_HEIGHT / 2 + 20, getString('hud.inventory.title'), {
      fontSize: '14px',
      fontStyle: 'bold',
      color: hexToCSS(PAL_YELLOW),
      align: 'center',
    });
    title.setOrigin(0.5, 0.5);
    this.container.add(title);

    // Equipment slots
    const slotStartY = py - PANEL_HEIGHT / 2 + 54;
    const slotX = px - PANEL_WIDTH / 2 + MARGIN + SLOT_SIZE / 2;
    const labelX = slotX + SLOT_SIZE / 2 + 10;

    // Weapon slot
    this.weaponSlotBg = this.createSlot(slotX, slotStartY);
    this.weaponLabel = this.createSlotLabel(labelX, slotStartY, EquipmentSlot.Weapon);

    // Armor slot
    const armorY = slotStartY + SLOT_SIZE + SLOT_GAP;
    this.armorSlotBg = this.createSlot(slotX, armorY);
    this.armorLabel = this.createSlotLabel(labelX, armorY, EquipmentSlot.Armor);

    // Accessory slot
    const accY = armorY + SLOT_SIZE + SLOT_GAP;
    this.accessorySlotBg = this.createSlot(slotX, accY);
    this.accessoryLabel = this.createSlotLabel(labelX, accY, EquipmentSlot.Accessory);

    // Weight display
    const weightY = py + PANEL_HEIGHT / 2 - 36;
    this.weightLabel = this.scene.add.text(px, weightY, '', {
      fontSize: '11px',
      color: hexToCSS(HUD_TEXT_SECONDARY),
      align: 'center',
    });
    this.weightLabel.setOrigin(0.5, 0.5);
    this.container.add(this.weightLabel);

    // Toggle hint
    const hintY = py + PANEL_HEIGHT / 2 - 16;
    const hint = this.scene.add.text(px, hintY, getString('hud.inventory.toggle'), {
      fontSize: '9px',
      color: hexToCSS(HUD_TEXT_SECONDARY),
      align: 'center',
    });
    hint.setOrigin(0.5, 0.5);
    this.container.add(hint);
  }

  private createSlot(x: number, y: number): Phaser.GameObjects.Rectangle {
    const slot = this.scene.add.rectangle(x, y, SLOT_SIZE, SLOT_SIZE, INVENTORY_SLOT_BG);
    slot.setStrokeStyle(1, INVENTORY_BORDER);
    this.container.add(slot);
    return slot;
  }

  private createSlotLabel(x: number, y: number, slot: EquipmentSlot): Phaser.GameObjects.Text {
    const slotName = getString(`hud.equipment.${slot}`);
    const label = this.scene.add.text(x, y, slotName, {
      fontSize: '11px',
      color: hexToCSS(HUD_TEXT_PRIMARY),
    });
    label.setOrigin(0, 0.5);
    this.container.add(label);
    return label;
  }

  private refresh(): void {
    const snap = this.snapshotProvider?.() ?? null;

    if (this.panel) {
      this.refreshC5(snap);
      return;
    }

    this.refreshSlot(this.weaponSlotBg, this.weaponLabel, EquipmentSlot.Weapon, snap?.equippedWeapon ?? '');
    this.refreshSlot(this.armorSlotBg, this.armorLabel, EquipmentSlot.Armor, snap?.equippedArmor ?? '');
    this.refreshSlot(this.accessorySlotBg, this.accessoryLabel, EquipmentSlot.Accessory, snap?.equippedAccessory ?? '');

    if (snap) {
      const weightColor = snap.encumbered ? hexToCSS(PAL_RED) : hexToCSS(HUD_TEXT_SECONDARY);
      this.weightLabel.setText(
        getString('hud.inventory.weight')
          .replace('{0}', String(snap.inventoryWeight))
          .replace('{1}', String(snap.inventoryCapacity)),
      );
      this.weightLabel.setColor(weightColor);
    } else {
      this.weightLabel.setText('');
    }
  }

  private refreshSlot(
    bg: Phaser.GameObjects.Rectangle,
    label: Phaser.GameObjects.Text,
    slot: EquipmentSlot,
    itemType: string,
  ): void {
    const slotName = getString(`hud.equipment.${slot}`);
    if (itemType) {
      const itemName = getString(`hud.item.${itemType}`) || itemType;
      label.setText(`${slotName}: ${itemName}`);
      bg.setFillStyle(SLOT_COLORS[slot] ?? INVENTORY_SLOT_BG);
    } else {
      label.setText(`${slotName}: ${getString('hud.equipment.empty_slot')}`);
      bg.setFillStyle(INVENTORY_SLOT_EMPTY);
    }
  }

  destroy(): void {
    this.panel?.destroy();
    this.panel = null;
    this.c5Rows.clear();
    this.c5Weight = null;
    this.container.destroy();
  }
}
